// +----------------------------------------------------------------------
// | 路由表
// | 公开：/login /register /decision/share/:token /topics*
// | 其余在 Layout 内，其中需要登录的用 <RequireAuth> 包一层
// +----------------------------------------------------------------------
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import useAuth from './hooks/useAuth';

import Home from './view/Home';
import Login from './view/Login';
import Register from './view/Register';
import Forgot from './view/Forgot';
import Reset from './view/Reset';
import Decision from './view/Decision';
import DecisionDetail from './view/DecisionDetail';
import DecisionHistory from './view/DecisionHistory';
import DecisionShare from './view/DecisionShare';
import Profile from './view/Profile';
import ProfileEdit from './view/ProfileEdit';
import Settings from './view/Settings';
import Memories from './view/Memories';
import Topics from './view/Topics';
import TopicDetail from './view/TopicDetail';
import Chat from './view/Chat';
import FreeCode from './view/FreeCode';
import Vip from './view/Vip';
import NotFound from './view/NotFound';

function LayoutShell() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function RequireAuth({ children }) {
  const { isLoggedIn, ready } = useAuth();
  const location = useLocation();

  if (!ready) {
    return <div className="page center muted">正在加载…</div>;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* 免登录页面 */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      {/* 找回密码：忘了密码的人正是登不进的那个，两条都必须免登录 */}
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset" element={<Reset />} />
      <Route path="/decision/share/:token" element={<DecisionShare />} />

      {/* 主站（带顶部导航） */}
      <Route element={<LayoutShell />}>
        <Route path="/" element={<Home />} />

        {/* 热门纠结：公共数据，免登录也能看 */}
        <Route path="/topics" element={<Topics />} />
        <Route path="/topics/:key" element={<TopicDetail />} />

        {/* AI 对话（Phase 9）：/chat 是新对话，/chat/:id 是某个会话。
            两条合一（:id? 可选参数）：发第一条消息会 navigate /chat → /chat/:id，
            拆成两条 Route 会导致整个聊天组件被卸载重挂——输入框失焦、
            手机端键盘收起、iOS 聚焦缩放回弹，看起来就是「页面莫名缩小」 */}
        <Route
          path="/chat/:id?"
          element={
            <RequireAuth>
              <Chat />
            </RequireAuth>
          }
        />

        {/* 领体验码（公众号导流落地页）：要登录——码要挂到账号上，
            不登录发出去没法限制「每人一张」 */}
        <Route
          path="/free-code"
          element={
            <RequireAuth>
              <FreeCode />
            </RequireAuth>
          }
        />

        {/* 想想会员（开通/续费）：从「我的」页那一行点进来。
            要登录 —— 会员是挂在账号上的 */}
        <Route
          path="/vip"
          element={
            <RequireAuth>
              <Vip />
            </RequireAuth>
          }
        />

        <Route
          path="/decision/:id"
          element={
            <RequireAuth>
              <Decision />
            </RequireAuth>
          }
        />
        <Route
          path="/decision/:id/result"
          element={
            <RequireAuth>
              <DecisionDetail />
            </RequireAuth>
          }
        />
        <Route
          path="/history"
          element={
            <RequireAuth>
              <DecisionHistory />
            </RequireAuth>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <Profile />
            </RequireAuth>
          }
        />
        {/* 编辑资料子页：从「我的」页「个人信息」行点进来，表单在这边 */}
        <Route
          path="/profile/edit"
          element={
            <RequireAuth>
              <ProfileEdit />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path="/memories"
          element={
            <RequireAuth>
              <Memories />
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
